import React, { useEffect, useRef, useState } from 'react';
import { X, ScanLine, CheckCircle, AlertCircle } from 'lucide-react';
import QRCode from 'qrcode';

const BACKEND = import.meta.env.VITE_API_URL || 'https://api.cloudairambo.com';

const APP_URL = window.location.origin;

interface Props {
  onClose: () => void;
  onProductScanned: (product: { name: string; description: string; rate: number; unit: string }) => void;
}

export default function BarcodeScannerModal({ onClose, onProductScanned }: Props) {
  const [manualBarcode, setManualBarcode] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'found' | 'notfound'>('idle');
  const [product, setProduct] = useState<any>(null);
  const [sessionId] = useState(() => Math.random().toString(36).slice(2, 10));
  const [qrDataUrl, setQrDataUrl] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const startPolling = () => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const res = await fetch(`${BACKEND}/scan-session/${sessionId}`);
        const data = await res.json();
        if (data.value) {
          clearInterval(pollRef.current!);
          setManualBarcode(data.value);
          lookup(data.value);
        }
      } catch {}
    }, 1000);
  };

  useEffect(() => {
    inputRef.current?.focus();
    const url = `${APP_URL}/scan?session=${sessionId}`;
    QRCode.toDataURL(url, { width: 160, margin: 1 }).then(setQrDataUrl);
    startPolling();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  const lookup = async (barcode: string) => {
    if (!barcode.trim()) return;
    setStatus('loading');
    try {
      const res = await fetch(`${BACKEND}/products/lookup?barcode=${encodeURIComponent(barcode.trim())}`);
      const data = await res.json();
      if (data.found) {
        setProduct(data.product);
        setStatus('found');
      } else {
        setProduct(null);
        setStatus('notfound');
      }
    } catch {
      setStatus('notfound');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') lookup(manualBarcode);
  };

  const handleAddToInvoice = () => {
    if (!product) return;
    onProductScanned({
      name: product.name,
      description: product.description || product.name,
      rate: product.rate || 0,
      unit: product.unit || 'pcs',
    });
    setManualBarcode('');
    setStatus('idle');
    setProduct(null);
    startPolling();
    inputRef.current?.focus();
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      background: 'rgba(0,0,0,0.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center'
    }}>
      <div style={{
        background: '#fff', borderRadius: 16, padding: 28,
        width: 580, boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
        fontFamily: 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
      }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <ScanLine size={20} color="#2563eb" />
            <span style={{ fontWeight: 700, fontSize: 16, color: '#0f172a' }}>Barcode Scanner</span>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8' }}>
            <X size={18} />
          </button>
        </div>

        <div style={{ display: 'flex', gap: 24 }}>
          {/* Left — manual / USB input */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{ fontSize: 12, color: '#64748b', marginBottom: 8 }}>
              Type barcode or use USB scanner — press Enter to search
            </p>

            <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
              <input
                ref={inputRef}
                value={manualBarcode}
                onChange={e => setManualBarcode(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="e.g. 8901234567890"
                style={{
                  flex: 1, minWidth: 0, padding: '10px 12px',
                  border: '2px solid #3b82f6', borderRadius: 8,
                  fontSize: 14, outline: 'none', fontFamily: 'monospace'
                }}
              />
              <button
                onClick={() => lookup(manualBarcode)}
                disabled={status === 'loading'}
                style={{
                  padding: '10px 16px', background: '#2563eb', color: '#fff',
                  border: 'none', borderRadius: 8, fontWeight: 700,
                  fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap'
                }}>
                {status === 'loading' ? '...' : 'Search'}
              </button>
            </div>

            {/* USB hint */}
            <div style={{
              background: '#f8fafc', border: '1px solid #e2e8f0',
              borderRadius: 8, padding: '8px 12px', marginBottom: 14,
              display: 'flex', alignItems: 'flex-start', gap: 8
            }}>
              <span style={{ fontSize: 15, flexShrink: 0, marginTop: 1 }}>🔌</span>
              <p style={{ fontSize: 12, color: '#64748b', margin: 0, lineHeight: 1.5 }}>
                <strong style={{ color: '#475569' }}>USB scanner:</strong> Plug in any HID barcode scanner,
                click the input above, and scan — it types the barcode automatically.
              </p>
            </div>

            {/* Result: Found */}
            {status === 'found' && product && (
              <div style={{
                background: '#f0fdf4', border: '1px solid #bbf7d0',
                borderRadius: 10, padding: 16, marginBottom: 16
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                  <CheckCircle size={16} color="#22c55e" />
                  <span style={{ fontWeight: 700, fontSize: 13, color: '#166534' }}>Product Found</span>
                </div>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>{product.name}</div>
                {product.brand && <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>{product.brand}</div>}
                <div style={{ display: 'flex', gap: 16, marginTop: 8, fontSize: 12 }}>
                  <span style={{ color: '#475569' }}>Rate: <strong>₹{product.rate}</strong></span>
                  <span style={{ color: '#475569' }}>Unit: <strong>{product.unit || 'pcs'}</strong></span>
                  {product.category && <span style={{ color: '#475569' }}>Cat: <strong>{product.category}</strong></span>}
                </div>
                <button
                  onClick={handleAddToInvoice}
                  style={{
                    marginTop: 12, width: '100%', padding: '9px 0',
                    background: '#16a34a', color: '#fff', border: 'none',
                    borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: 'pointer'
                  }}>
                  + Add to Invoice Table
                </button>
              </div>
            )}

            {/* Result: Not Found */}
            {status === 'notfound' && (
              <div style={{
                background: '#fef2f2', border: '1px solid #fecaca',
                borderRadius: 10, padding: 12, marginBottom: 16,
                display: 'flex', alignItems: 'center', gap: 8
              }}>
                <AlertCircle size={16} color="#ef4444" />
                <span style={{ fontSize: 13, color: '#991b1b', fontWeight: 600 }}>
                  Barcode not found. Add it in Product Catalog first.
                </span>
              </div>
            )}
          </div>

          {/* Right — mobile QR */}
          <div style={{
            width: 180, flexShrink: 0, display: 'flex', flexDirection: 'column',
            alignItems: 'center', gap: 10,
            borderLeft: '1px solid #e2e8f0', paddingLeft: 20
          }}>
            <p style={{ fontSize: 11, color: '#64748b', textAlign: 'center', margin: 0, lineHeight: 1.5 }}>
              📱 Scan with mobile<br />on same WiFi
            </p>
            {qrDataUrl ? (
              <img
                src={qrDataUrl}
                width={148}
                height={148}
                style={{ borderRadius: 8, border: '1px solid #e2e8f0' }}
                alt="QR code for mobile scan"
              />
            ) : (
              <div style={{
                width: 148, height: 148, background: '#f1f5f9', borderRadius: 8,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 11, color: '#94a3b8'
              }}>
                Generating...
              </div>
            )}
            <div style={{
              fontSize: 10, color: '#94a3b8', textAlign: 'center',
              fontFamily: 'monospace', wordBreak: 'break-all'
            }}>
              Session: {sessionId}
            </div>
            <div style={{
              width: '100%', padding: '5px 8px',
              background: '#f0fdf4', border: '1px solid #bbf7d0',
              borderRadius: 6, fontSize: 10, color: '#166534', textAlign: 'center'
            }}>
              ⏳ Waiting for mobile scan...
            </div>
          </div>
        </div>

        <p style={{ fontSize: 11, color: '#94a3b8', textAlign: 'center', marginTop: 14, marginBottom: 0 }}>
          Scan multiple items — each scan adds a new row instantly
        </p>
      </div>
    </div>
  );
}
